import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import api from "../api";

const AdminPanel = () => {
  const { lang } = useI18n();
  const [token, setToken] = useState(() => localStorage.getItem("sne_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("plan_4mo");
  const [customerLabel, setCustomerLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [codes, setCodes] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/catalog/pricing").then((r) => setPackages(r.data.packages));
  }, []);

  const tryAuth = async () => {
    setError("");
    try {
      const res = await api.get("/admin/codes", { headers: { "X-Admin-Token": token } });
      setCodes(res.data.codes);
      setAuthed(true);
      localStorage.setItem("sne_admin_token", token);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Invalid admin token" : "Connection error");
    }
  };

  const refresh = async () => {
    try {
      const res = await api.get("/admin/codes", { headers: { "X-Admin-Token": token } });
      setCodes(res.data.codes);
    } catch (e) { /* ignore */ }
  };

  const generate = async () => {
    setError(""); setGenerated(null); setGenerating(true);
    try {
      const res = await api.post(
        "/admin/generate-code",
        { package_id: selectedPkg, customer_label: customerLabel || null, notes: notes || null },
        { headers: { "X-Admin-Token": token } },
      );
      setGenerated(res.data);
      setCustomerLabel(""); setNotes("");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error generating code");
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async (code) => {
    if (!window.confirm(`Revoke ${code}?`)) return;
    await api.delete(`/admin/codes/${encodeURIComponent(code)}`, { headers: { "X-Admin-Token": token } });
    await refresh();
  };

  const copy = (s) => navigator.clipboard.writeText(s);

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-20">
        <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-3">// ADMIN</div>
        <h1 className="font-heading text-4xl mb-6">Admin Panel</h1>
        <label className="sne-label">Admin Token</label>
        <input className="sne-input font-mono"
               type="password"
               value={token}
               onChange={(e) => setToken(e.target.value)}
               data-testid="admin-token-input" />
        {error && <div className="mt-3 text-[#FF3B30] font-mono text-xs" data-testid="admin-token-error">{error}</div>}
        <button className="btn-primary w-full mt-5" onClick={tryAuth} data-testid="admin-login-btn">Enter →</button>
        <div className="mt-6">
          <Link to="/" className="font-mono text-xs text-white/50 hover:text-white">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-xs tracking-widest text-[#FF3B30]">// ADMIN</div>
          <h1 className="font-heading text-4xl">Code Generator</h1>
        </div>
        <button onClick={() => { localStorage.removeItem("sne_admin_token"); setAuthed(false); setToken(""); }}
                className="btn-secondary" data-testid="admin-logout">
          Log out
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="sne-card">
          <h2 className="font-heading text-2xl mb-4">Generate New Code</h2>
          <label className="sne-label">Package</label>
          <select className="sne-select" value={selectedPkg} onChange={(e) => setSelectedPkg(e.target.value)} data-testid="admin-pkg-select">
            {packages.map((p) => (
              <option key={p.key} value={p.key}>{p.label_en} (€{p.amount})</option>
            ))}
          </select>

          <label className="sne-label mt-4">Customer / Club (optional)</label>
          <input className="sne-input" placeholder="e.g. CSM Volei Bucuresti — U17 team"
                 value={customerLabel} onChange={(e) => setCustomerLabel(e.target.value)}
                 data-testid="admin-customer" />

          <label className="sne-label mt-4">Internal Notes (optional)</label>
          <input className="sne-input" placeholder="e.g. Paid via bank transfer, 2026-05-30"
                 value={notes} onChange={(e) => setNotes(e.target.value)}
                 data-testid="admin-notes" />

          <button onClick={generate} disabled={generating} className="btn-primary w-full mt-5" data-testid="admin-generate-btn">
            {generating ? "Generating..." : "Generate Code →"}
          </button>

          {error && <div className="mt-3 text-[#FF3B30] font-mono text-xs" data-testid="admin-error">{error}</div>}

          {generated && (
            <div className="mt-5 p-4" style={{ background: "#0F2A12", border: "1px solid #34C759" }} data-testid="admin-generated">
              <div className="font-mono text-[10px] tracking-widest text-[#34C759] mb-1">✓ GENERATED</div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-2xl text-white tracking-widest" data-testid="admin-generated-code">{generated.code}</span>
                <button onClick={() => copy(generated.code)}
                        className="font-mono text-[10px] tracking-widest px-2 py-1 border border-[#34C759] text-[#34C759]"
                        style={{ background: "transparent" }}>
                  ⎘ Copy
                </button>
              </div>
              <div className="mt-3 font-mono text-xs text-white/70">
                {generated.plans_quota} plans · {generated.duration_days} days · expires {generated.expires_at?.split("T")[0]}
              </div>
            </div>
          )}
        </div>

        <div className="sne-card">
          <h2 className="font-heading text-2xl mb-2">Character Set Reference</h2>
          <p className="text-sm text-white/70 mb-4">Codes are random permutations of one of these sets (mixed case):</p>
          <div className="space-y-3 font-mono text-sm">
            <div>
              <div className="text-xs tracking-widest text-white/50 mb-1">PLAN_4MO (€50, 10 plans)</div>
              <div className="text-white">a · i · u · l · e · 1 · 9 · 7 · 4</div>
            </div>
            <div>
              <div className="text-xs tracking-widest text-white/50 mb-1">PLAN_1Y (€100, 25 plans)</div>
              <div className="text-white">b · i · u · l · e · 1 · 9 · 7 · 4</div>
            </div>
          </div>
          <div className="mt-5 p-3" style={{ background: "#181111", borderLeft: "2px solid #FF3B30" }}>
            <div className="font-mono text-[10px] tracking-widest text-[#FF3B30] mb-1">ℹ INFO</div>
            <div className="text-xs text-white/80">
              Codes are case-insensitive on verification. Each set has 9! = 362,880 permutations × 32 case variants ≈ 11.6M unique codes per plan.
            </div>
          </div>
        </div>
      </div>

      {/* Existing codes table */}
      <div className="sne-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl">Issued Codes ({codes.length})</h2>
          <button onClick={refresh} className="btn-secondary" data-testid="admin-refresh">↻ Refresh</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-widest text-white/40">
                <th className="py-2 pr-3">CODE</th>
                <th>PLAN</th>
                <th>QUOTA</th>
                <th>CUSTOMER</th>
                <th>ISSUED</th>
                <th>EXPIRES</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.code} style={{ borderTop: "1px solid #1F1F1F" }} data-testid={`admin-row-${c.code}`}>
                  <td className="py-2 pr-3 font-mono text-white tracking-widest">{c.code_display || c.code}</td>
                  <td className="font-mono text-xs text-white/70">{c.package_id}</td>
                  <td className="font-mono text-xs">
                    {c.plans_remaining === -1 ? "∞" : `${c.plans_remaining}/${c.plans_total}`}
                  </td>
                  <td className="font-mono text-xs text-white/70">{c.customer_label || "—"}</td>
                  <td className="font-mono text-xs text-white/50">{c.issued_at?.split("T")[0] || "—"}</td>
                  <td className="font-mono text-xs text-white/50">{c.expires_at?.split("T")[0] || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => copy(c.code_display || c.code)} className="font-mono text-[10px] text-white/60 hover:text-white mr-2">⎘</button>
                    {!c.service_code && (
                      <button onClick={() => revoke(c.code)} className="font-mono text-[10px] text-[#FF3B30] hover:underline">revoke</button>
                    )}
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr><td colSpan="7" className="py-6 text-center text-white/40 text-sm">No codes issued yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
